import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../../services/apiClient";

type Category = { _id: string; name: string };

const todayLocalYMD = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

type AddExpenseValues = {
  description: string;
  cost: number; // positive number
  date: string; // YYYY-MM-DD
  category?: string; // category name
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** Called with validated values (no date/year). Modal shows a busy state while awaiting this. */
  onSubmit: (values: AddExpenseValues) => Promise<void> | void;
  title?: string;
  confirmLabel?: string;
  initialValues?: Partial<AddExpenseValues>;
};

const AddExpenseModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  title = "Add Expense",
  confirmLabel = "Save",
  initialValues,
}) => {
  const [description, setDescription] = useState(
    initialValues?.description || ""
  );
  const [costRaw, setCostRaw] = useState(
    typeof initialValues?.cost === "number" ? String(initialValues.cost) : ""
  );
  const [date, setDate] = useState(initialValues?.date || "");
  const [category, setCategory] = useState(initialValues?.category || "");

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // categories
  const [loadingCats, setLoadingCats] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [catQuery, setCatQuery] = useState("");

  const loadCategories = async () => {
    try {
      setLoadingCats(true);
      const { data } = await apiClient.get("/finance/expense-categories");
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      // soft-fail; user can still type a free-text category
      setCategories([]);
    } finally {
      setLoadingCats(false);
    }
  };

  useEffect(() => {
    if (open) {
      setDescription(initialValues?.description || "");
      setCostRaw(
        typeof initialValues?.cost === "number"
          ? String(initialValues.cost)
          : ""
      );
      setDate(
        initialValues?.date || todayLocalYMD()
      );
      setCategory(initialValues?.category || "");
      setError(null);
      setBusy(false);
      loadCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cost = useMemo(() => {
    const v = Number.parseFloat(costRaw);
    return Number.isFinite(v) ? v : NaN;
  }, [costRaw]);

  const canSubmit =
    description.trim().length > 0 &&
    Number.isFinite(cost) &&
    cost > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(date);

  const filteredCats = useMemo(() => {
    const q = catQuery.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => c.name.toLowerCase().includes(q));
  }, [categories, catQuery]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === "Escape") onClose();
  };

  const submit = async () => {
    setError(null);
    if (!canSubmit) {
      setError("Please enter a description, date, and positive amount.");
      return;
    }
    try {
      setBusy(true);
      await onSubmit({
        description: description.trim(),
        cost,
        date,
        category: category?.trim() || undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err?.message || "Failed to add expense.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      onKeyDown={handleKeyDown}
      aria-modal
      role="dialog"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Card */}
      <div className="relative bg-white rounded-lg shadow-xl border border-slate-200 w-[92vw] max-w-md p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-slate-600 mb-1">
              Description
            </label>
            <input
              type="text"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              placeholder="e.g., Booth fee"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-xs text-slate-600 mb-1">Date</label>
            <input
              type="date"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs text-slate-600 mb-1">Amount</label>
            <input
              inputMode="decimal"
              type="text"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              placeholder="e.g., 125.50"
              value={costRaw}
              onChange={(e) =>
                setCostRaw(
                  e.target.value
                    .replace(/[^0-9.]/g, "")
                    .replace(/(\..*)\./g, "$1")
                )
              }
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="block text-xs text-slate-600 mb-1">
                Category
              </label>
              {!loadingCats && categories.length > 0 && (
                <span className="text-[11px] text-slate-500">
                  {categories.length} available
                </span>
              )}
            </div>

            {/* combo: simple input + dropdown list of existing categories */}
            <div className="space-y-2">
              <input
                type="text"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                placeholder={
                  loadingCats
                    ? "Loading categories…"
                    : "Type or pick a category"
                }
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                list="add-expense-cat-datalist"
              />
              {/* Search within list */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="Search categories…"
                  value={catQuery}
                  onChange={(e) => setCatQuery(e.target.value)}
                />
                <button
                  type="button"
                  onClick={loadCategories}
                  className="px-2.5 py-1.5 rounded-md border border-slate-300 text-xs hover:bg-slate-50"
                  title="Refresh categories"
                >
                  Refresh
                </button>
              </div>

              {/* Lightweight list */}
              <div className="max-h-40 overflow-auto border border-slate-200 rounded-md">
                {loadingCats ? (
                  <div className="p-3 text-sm text-slate-500">Loading…</div>
                ) : filteredCats.length === 0 ? (
                  <div className="p-3 text-sm text-slate-500">
                    No categories found
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {filteredCats.map((c) => {
                      const selected = c.name === category;
                      return (
                        <li key={c._id}>
                          <button
                            type="button"
                            onClick={() => setCategory(c.name)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                              selected ? "bg-blue-50" : ""
                            }`}
                          >
                            {c.name}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded p-2">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 rounded-md border border-slate-300 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit || busy}
            className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm border border-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? "Saving…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddExpenseModal;
